import { gsap } from 'gsap';

interface MagicBentoOptions {
  enableParticles?: boolean;
  enableTilt?: boolean;
  enableMagnetism?: boolean;
  enableClickRipple?: boolean;
  particleCount?: number;
  glowColor?: string; // RGB format: "255, 255, 255"
}

const DEFAULT_OPTIONS: Required<MagicBentoOptions> = {
  enableParticles: true,
  enableTilt: true,
  enableMagnetism: true,
  enableClickRipple: true,
  particleCount: 12,
  glowColor: '255, 255, 255', // White for black/white theme
};

function createParticle(x: number, y: number, color: string) {
  const el = document.createElement('div');
  el.style.cssText = `
    position: absolute;
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: rgba(${color}, 1);
    box-shadow: 0 0 6px rgba(${color}, 0.6);
    pointer-events: none;
    z-index: 100;
    left: ${x}px;
    top: ${y}px;
  `;
  return el;
}

export function magicBento(node: HTMLElement, options: MagicBentoOptions = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // Disable on mobile for performance
  const isMobile = window.innerWidth <= 768;
  if (isMobile) {
    return {
      update() {},
      destroy() {}
    };
  }

  const particles: HTMLElement[] = [];
  const timeouts: ReturnType<typeof setTimeout>[] = [];
  let isHovered = false;
  let magnetismAnimation: gsap.core.Tween | null = null;

  function clearAllParticles() {
    timeouts.forEach(clearTimeout);
    timeouts.length = 0;
    magnetismAnimation?.kill();

    particles.forEach(particle => {
      gsap.to(particle, {
        scale: 0,
        opacity: 0,
        duration: 0.3,
        ease: 'back.in(1.7)',
        onComplete: () => {
          particle.parentNode?.removeChild(particle);
        }
      });
    });
    particles.length = 0;
  }

  function animateParticles() {
    if (!isHovered) return;

    const { width, height } = node.getBoundingClientRect();

    for (let i = 0; i < opts.particleCount; i++) {
      const timeoutId = setTimeout(() => {
        if (!isHovered) return;

        const x = Math.random() * width;
        const y = Math.random() * height;
        const particle = createParticle(x, y, opts.glowColor);

        node.appendChild(particle);
        particles.push(particle);

        gsap.fromTo(
          particle,
          { scale: 0, opacity: 0 },
          { scale: 1, opacity: 1, duration: 0.3, ease: 'back.out(1.7)' }
        );

        gsap.to(particle, {
          x: (Math.random() - 0.5) * 100,
          y: (Math.random() - 0.5) * 100,
          rotation: Math.random() * 360,
          duration: 2 + Math.random() * 2,
          ease: 'none',
          repeat: -1,
          yoyo: true
        });

        gsap.to(particle, {
          opacity: 0.3,
          duration: 1.5,
          ease: 'power2.inOut',
          repeat: -1,
          yoyo: true
        });
      }, i * 100);

      timeouts.push(timeoutId);
    }
  }

  function handleMouseEnter() {
    isHovered = true;
    if (opts.enableParticles) {
      animateParticles();
    }

    if (opts.enableTilt) {
      gsap.to(node, {
        rotateX: 5,
        rotateY: 5,
        duration: 0.3,
        ease: 'power2.out',
        transformPerspective: 1000
      });
    }
  }

  function handleMouseLeave() {
    isHovered = false;
    clearAllParticles();

    if (opts.enableTilt) {
      gsap.to(node, {
        rotateX: 0,
        rotateY: 0,
        duration: 0.3,
        ease: 'power2.out'
      });
    }

    if (opts.enableMagnetism) {
      gsap.to(node, {
        x: 0,
        y: 0,
        duration: 0.3,
        ease: 'power2.out'
      });
    }
  }

  function handleMouseMove(e: MouseEvent) {
    const rect = node.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    if (opts.enableTilt) {
      const rotateX = ((y - centerY) / centerY) * -10;
      const rotateY = ((x - centerX) / centerX) * 10;

      gsap.to(node, {
        rotateX,
        rotateY,
        duration: 0.1,
        ease: 'power2.out',
        transformPerspective: 1000
      });
    }

    if (opts.enableMagnetism) {
      const magnetX = (x - centerX) * 0.05;
      const magnetY = (y - centerY) * 0.05;

      magnetismAnimation = gsap.to(node, {
        x: magnetX,
        y: magnetY,
        duration: 0.3,
        ease: 'power2.out'
      });
    }
  }

  function handleClick(e: MouseEvent) {
    if (!opts.enableClickRipple) return;

    const rect = node.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const maxDistance = Math.max(
      Math.hypot(x, y),
      Math.hypot(x - rect.width, y),
      Math.hypot(x, y - rect.height),
      Math.hypot(x - rect.width, y - rect.height)
    );

    const ripple = document.createElement('div');
    ripple.style.cssText = `
      position: absolute;
      width: ${maxDistance * 2}px;
      height: ${maxDistance * 2}px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(${opts.glowColor}, 0.2) 0%, rgba(${opts.glowColor}, 0.1) 30%, transparent 70%);
      left: ${x - maxDistance}px;
      top: ${y - maxDistance}px;
      pointer-events: none;
      z-index: 1000;
    `;

    node.appendChild(ripple);

    gsap.fromTo(
      ripple,
      { scale: 0, opacity: 1 },
      {
        scale: 1,
        opacity: 0,
        duration: 0.8,
        ease: 'power2.out',
        onComplete: () => ripple.remove()
      }
    );
  }

  // Set up initial styles
  node.style.position = 'relative';
  node.style.overflow = 'hidden';
  node.style.transformStyle = 'preserve-3d';

  // Add event listeners
  node.addEventListener('mouseenter', handleMouseEnter);
  node.addEventListener('mouseleave', handleMouseLeave);
  node.addEventListener('mousemove', handleMouseMove);
  node.addEventListener('click', handleClick);

  return {
    update(newOptions: MagicBentoOptions) {
      Object.assign(opts, newOptions);
    },
    destroy() {
      node.removeEventListener('mouseenter', handleMouseEnter);
      node.removeEventListener('mouseleave', handleMouseLeave);
      node.removeEventListener('mousemove', handleMouseMove);
      node.removeEventListener('click', handleClick);
      clearAllParticles();
    }
  };
}
